/**
 * F2.2c / F2.3b / F2.5a2 — Miroir lecture seule « Récompenses » pour Mon programme (F1).
 * Source de vérité : GET catalogue F2.1 uniquement (pas les champs legacy).
 *
 * F2.3b : 0 / 1 / N ACTIVE € — jamais de .find() comme « récompense principale ».
 * F2.5a2 : ACTIVE % inclus dans l’échangeable / miroir multi-types.
 */

function effectsOf(row) {
  if (!row || typeof row !== 'object') return [];
  if (Array.isArray(row.effets)) return row.effets;
  if (Array.isArray(row.effects)) return row.effects;
  return [];
}

export function isReductionEurActive(row) {
  if (!row || row.statut !== 'ACTIVE') return false;
  return effectsOf(row).some((e) => e && e.type === 'REDUCTION_EUR');
}

export function isReductionPourcentActive(row) {
  if (!row || row.statut !== 'ACTIVE') return false;
  return effectsOf(row).some((e) => e && e.type === 'REDUCTION_POURCENT');
}

export function isExchangeableActive(row) {
  return isReductionEurActive(row) || isReductionPourcentActive(row);
}

export function extractReductionEurValeur(row) {
  const eur = effectsOf(row).find((e) => e && e.type === 'REDUCTION_EUR');
  if (!eur) return null;
  const v = Number(eur.valeur);
  return Number.isFinite(v) && v > 0 ? v : null;
}

export function extractReductionPourcentValeur(row) {
  const pct = effectsOf(row).find((e) => e && e.type === 'REDUCTION_POURCENT');
  if (!pct) return null;
  const v = Number(pct.valeur);
  return Number.isFinite(v) && v > 0 && v <= 100 ? v : null;
}

/** Définitions « configurées » pour le compteur F1 : hors ARCHIVEE. */
export function filterConfiguredRecompenses(list) {
  const items = Array.isArray(list) ? list : [];
  return items.filter((r) => r && typeof r === 'object' && r.statut !== 'ARCHIVEE');
}

/** Toutes les ACTIVE € (pas la première seule). */
export function listActiveEuroRecompenses(list) {
  return filterConfiguredRecompenses(list).filter((r) => isReductionEurActive(r));
}

export function listActivePourcentRecompenses(list) {
  return filterConfiguredRecompenses(list).filter((r) => isReductionPourcentActive(r));
}

export function listActiveExchangeableRecompenses(list) {
  return filterConfiguredRecompenses(list).filter((r) => isExchangeableActive(r));
}

/**
 * @deprecated F2.3b — préférer listActiveEuroRecompenses ; ne plus utiliser comme « principale ».
 * Conservé : retourne la ligne unique si N===1, sinon null.
 */
export function findActiveEuroRecompense(list) {
  const actives = listActiveEuroRecompenses(list);
  return actives.length === 1 ? actives[0] : null;
}

/**
 * Phrase miroir métier EUR : « 100 points → 5 € offerts »
 */
export function formatActiveEuroMirrorPhrase(row) {
  if (!row) return null;
  const pts = Number(row.coutPoints);
  const euros = extractReductionEurValeur(row);
  if (!Number.isFinite(pts) || pts <= 0 || euros == null) return null;
  const ptsLabel = Math.trunc(pts) > 1 ? 'points' : 'point';
  const eurosLabel = Number.isInteger(euros) ? String(euros) : String(euros);
  return `${Math.trunc(pts)} ${ptsLabel} → ${eurosLabel} € offerts`;
}

/**
 * Phrase miroir % : « 200 points → 10 % de réduction »
 */
export function formatActivePourcentMirrorPhrase(row) {
  if (!row) return null;
  const pts = Number(row.coutPoints);
  const pct = extractReductionPourcentValeur(row);
  if (!Number.isFinite(pts) || pts <= 0 || pct == null) return null;
  const ptsLabel = Math.trunc(pts) > 1 ? 'points' : 'point';
  const pctLabel = Number.isInteger(pct) ? String(pct) : String(pct);
  return `${Math.trunc(pts)} ${ptsLabel} → ${pctLabel} % de réduction`;
}

export function formatActiveRewardMirrorPhrase(row) {
  if (isReductionPourcentActive(row)) return formatActivePourcentMirrorPhrase(row);
  if (isReductionEurActive(row)) return formatActiveEuroMirrorPhrase(row);
  return null;
}

export function formatConfiguredCountLabel(count) {
  const n = Number(count);
  if (!Number.isFinite(n) || n < 0) return null;
  if (n === 0) return null;
  if (n === 1) return '1 récompense configurée';
  return `${n} récompenses configurées`;
}

export function formatActiveEuroCountPhrase(count) {
  const n = Number(count);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n === 1) return null;
  if (n === 2) return '2 récompenses sont actuellement échangeables';
  return `${n} récompenses sont actuellement échangeables`;
}

/**
 * État miroir UI / résumé / aperçu.
 */
export function buildRecompenseProgrammeMirror(list, { loadError = false, loading = false } = {}) {
  if (loading) {
    return {
      status: 'loading',
      countConfigured: null,
      countLabel: null,
      activeEuro: null,
      activeEuroCount: 0,
      activeEuros: [],
      activeExchangeables: [],
      activeExchangeableCount: 0,
      glanceRows: [],
      multiActive: false,
      multiActivePhrase: null,
      mirrorPhrase: null,
      hasActiveEuro: false,
      exchangeable: false,
      empty: false,
      errorMessage: null,
      emptyTitle: null,
      emptyBody: null,
      noActiveHint: null,
      clientChoiceNotReadyHint: null,
    };
  }

  if (loadError) {
    return {
      status: 'error',
      countConfigured: null,
      countLabel: null,
      activeEuro: null,
      activeEuroCount: 0,
      activeEuros: [],
      activeExchangeables: [],
      activeExchangeableCount: 0,
      glanceRows: [],
      multiActive: false,
      multiActivePhrase: null,
      mirrorPhrase: null,
      hasActiveEuro: false,
      exchangeable: false,
      empty: false,
      errorMessage: 'Impossible de charger les récompenses pour le moment.',
      emptyTitle: null,
      emptyBody: null,
      noActiveHint: null,
      clientChoiceNotReadyHint: null,
    };
  }

  const configured = filterConfiguredRecompenses(list);
  const countConfigured = configured.length;
  const activeEuros = listActiveEuroRecompenses(configured);
  const activeEuroCount = activeEuros.length;
  const activeExchangeables = listActiveExchangeableRecompenses(configured);
  const activeExchangeableCount = activeExchangeables.length;
  const multiActive = activeExchangeableCount > 1;
  const activeEuro = activeEuroCount === 1 ? activeEuros[0] : null;
  const singleExchangeable =
    activeExchangeableCount === 1 ? activeExchangeables[0] : null;
  const mirrorPhrase = singleExchangeable
    ? formatActiveRewardMirrorPhrase(singleExchangeable)
    : null;
  const multiActivePhrase = multiActive
    ? formatActiveEuroCountPhrase(activeExchangeableCount)
    : null;
  const hasActiveEuro = activeEuroCount >= 1;
  const exchangeable = activeExchangeableCount >= 1;
  const empty = countConfigured === 0;

  return {
    status: 'ok',
    countConfigured,
    countLabel: formatConfiguredCountLabel(countConfigured),
    activeEuro,
    activeEuroCount,
    activeEuros,
    activeExchangeables,
    activeExchangeableCount,
    /** Lignes catalogue non archivées pour cartes coup d’œil (données réelles uniquement). */
    glanceRows: configured,
    multiActive,
    multiActivePhrase,
    mirrorPhrase,
    hasActiveEuro,
    exchangeable,
    empty,
    errorMessage: null,
    emptyTitle: empty ? 'Aucune récompense n’est encore configurée.' : null,
    emptyBody: empty
      ? 'Les clients peuvent cumuler des points, mais ils ne peuvent pas encore les échanger.'
      : null,
    noActiveHint:
      !empty && !exchangeable
        ? 'Aucune récompense n’est actuellement échangeable.'
        : null,
    clientChoiceNotReadyHint: multiActive
      ? 'Plusieurs récompenses sont actives. Le choix au paiement côté client est disponible.'
      : null,
  };
}

/** CTA commun */
export const MANAGE_RECOMPENSES_CTA = 'Gérer mes récompenses';
